import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Empty, Input, InputNumber, Modal, Popconfirm, Select,
  Space, Spin, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  ApartmentOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
  ReloadOutlined, SaveOutlined,
} from '@ant-design/icons';
import {
  getAvailableModules, listPlatformDocs, savePlatformDoc, deletePlatformDoc,
} from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
import AppCard from '../../../components/cards/AppCard';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// =============================================================================
// Workflows — the Admin surface for platformWorkflows metadata: event-driven
// behavior (module, event, condition, ordered actions) executed by the
// backend Workflow Engine. This is where the jobs/candidates/submissions
// create flows live after migration — and where ANY module's automation is
// authored, with zero backend code.
// =============================================================================

const EVENTS = ['beforeCreate', 'afterCreate', 'beforeUpdate', 'afterUpdate', 'beforeDelete', 'afterDelete'];
// Mirrors the backend Action Registry (workflowEngine.go) — validated server-side too.
const ACTION_TYPES = [
  'sanitizeFields', 'generateSequence', 'duplicateCheck', 'copyFields', 'arrayEnrich',
  'setDefaults', 'resolve', 'updateRelated', 'notify', 'invalidateCache', 'webhook',
];

function pretty(v) { return JSON.stringify(v ?? {}, null, 2); }

// One action row: a type select + its config as JSON (config shapes match the
// Automations strategies exactly — see the plan doc).
function ActionRow({ action, onChange, onRemove }) {
  const [text, setText] = useState(() => pretty(action.config));
  const [bad, setBad] = useState(false);
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Select
          size="small" style={{ width: 200 }} showSearch value={action.type || undefined}
          placeholder="action type" options={ACTION_TYPES.map((t) => ({ value: t }))}
          onChange={(v) => onChange({ ...action, type: v })}
        />
        {bad && <Tag color="red">invalid JSON</Tag>}
        <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }} onClick={onRemove} />
      </div>
      <Input.TextArea
        rows={Math.min(14, Math.max(3, text.split('\n').length))}
        value={text}
        className="fg-mono-input"
        style={{ marginTop: 8, fontSize: 12 }}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange({ ...action, config: JSON.parse(e.target.value || '{}') });
            setBad(false);
          } catch { setBad(true); }
        }}
      />
    </div>
  );
}

function WorkflowDrawer({ open, doc, moduleOptions, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    key: '', module: '', event: 'afterCreate', order: 10, isActive: true,
    condition: {}, actions: [],
    ...(doc ?? {}),
  }));
  const [condText, setCondText] = useState(() => pretty(doc?.condition));
  const [condBad, setCondBad] = useState(false);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.key?.trim()) return message.error('Key is required');
    if (!draft.module?.trim()) return message.error('Module is required');
    if (condBad) return message.error('Condition JSON is invalid');
    if (!(draft.actions ?? []).length) return message.error('At least one action is required');
    if ((draft.actions ?? []).some((a) => !a.type)) return message.error('Every action needs a type');
    const rest = { ...draft };
    delete rest.id;
    delete rest._id;
    onSave(rest);
  };

  return (
    <Modal
      open={open} onCancel={onClose} width={840} centered destroyOnClose maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><ApartmentOutlined /> {doc ? 'Edit Workflow' : 'New Workflow'}</span>}
      footer={<div className="fg-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Save Workflow</Button></div>}
    >
      <div className="fg-form-section">
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Key</Text>
            <Input size="small" placeholder="jobs-create-after" value={draft.key ?? ''} onChange={(e) => set('key', e.target.value)} className="fg-mono-input" disabled={!!doc} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Module</Text>
            <Select size="small" style={{ width: '100%' }} showSearch value={draft.module || undefined} options={moduleOptions} onChange={(v) => set('module', v)} placeholder="jobs" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Event</Text>
            <Select size="small" style={{ width: '100%' }} value={draft.event} options={EVENTS.map((e) => ({ value: e }))} onChange={(v) => set('event', v)} />
          </div>
          <div style={{ width: 90 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Order</Text>
            <InputNumber size="small" style={{ width: '100%' }} value={draft.order ?? 10} onChange={(v) => set('order', v ?? 10)} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <Switch size="small" checked={draft.isActive !== false} onChange={(v) => set('isActive', v)} />
          <Text style={{ fontSize: 13 }}>Active</Text>
        </div>

        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Condition (all must match the payload; empty = always run) {condBad && <Tag color="red">invalid JSON</Tag>}
          </Text>
          <Input.TextArea
            rows={2} value={condText} className="fg-mono-input" style={{ fontSize: 12 }}
            onChange={(e) => {
              setCondText(e.target.value);
              try { set('condition', JSON.parse(e.target.value || '{}')); setCondBad(false); } catch { setCondBad(true); }
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <Text strong style={{ fontSize: 13 }}>Actions (run in order)</Text>
          <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => set('actions', [...(draft.actions ?? []), { type: '', config: {} }])}>Add Action</Button>
        </div>
        {(draft.actions ?? []).map((a, i) => (
          <ActionRow
            key={i} action={a}
            onChange={(next) => set('actions', draft.actions.map((x, idx) => (idx === i ? next : x)))}
            onRemove={() => set('actions', draft.actions.filter((_, idx) => idx !== i))}
          />
        ))}
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
          Config supports <code>{'{{field.path}}'}</code>, <code>{'{{now}}'}</code>, <code>{'{{__userId}}'}</code>,{' '}
          <code>{'{{__userName}}'}</code>, and <code>{'{{hierarchy.<name>}}'}</code> from a <code>resolve</code> action.
        </Text>
      </div>
    </Modal>
  );
}

export default function WorkflowsSection() {
  const [docs, setDocs] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null); // { doc } | { doc: null }
  const [moduleFilter, setModuleFilter] = useState('');

  const [refreshKey, setRefreshKey] = useState(0);

  // The effect performs only async state updates; the Reload button (and every
  // save/delete) sets the spinner synchronously in its own handler, then bumps
  // refreshKey to re-run this fetch.
  useEffect(() => {
    let cancelled = false;
    listPlatformDocs('workflows')
      .then((d) => { if (!cancelled) setDocs(d); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load workflows'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    getAvailableModules()
      .then((raw) => setModules(normalizeModuleOptions(raw).map(({ value, label }) => ({ value, label }))))
      .catch(() => setModules([]));
  }, []);

  const load = () => {
    setLoading(true); setError('');
    setRefreshKey((k) => k + 1);
  };

  async function handleSave(doc) {
    try {
      await savePlatformDoc('workflows', doc);
      message.success(`Workflow "${doc.key}" saved`);
      setDraft(null);
      load();
    } catch (e) {
      message.error(e?.message || 'Could not save workflow');
    }
  }

  async function handleDelete(key) {
    try {
      await deletePlatformDoc('workflows', key);
      message.success(`Workflow "${key}" deleted`);
      load();
    } catch (e) {
      message.error(e?.message || 'Could not delete workflow');
    }
  }

  async function toggleActive(doc, active) {
    const rest = { ...doc };
    delete rest.id;
    delete rest._id;
    try {
      await savePlatformDoc('workflows', { ...rest, isActive: active });
      load();
    } catch (e) {
      message.error(e?.message || 'Could not update workflow');
    }
  }

  const rows = useMemo(
    () => docs.filter((d) => !moduleFilter || d.module === moduleFilter),
    [docs, moduleFilter],
  );

  const columns = [
    { title: 'Key', dataIndex: 'key', key: 'key', render: (v) => <code className="fg-table-code">{v}</code> },
    { title: 'Module', dataIndex: 'module', key: 'module', width: 130, render: (v) => <Tag color="geekblue">{v}</Tag> },
    { title: 'Event', dataIndex: 'event', key: 'event', width: 130, render: (v) => <Tag color={String(v).startsWith('before') ? 'purple' : 'green'}>{v}</Tag> },
    {
      title: 'Actions', key: 'acts',
      render: (_, d) => (
        <Space size={4} wrap>
          {(d.actions ?? []).map((a, i) => <Tag key={i} style={{ margin: 0 }}>{a.type}</Tag>)}
        </Space>
      ),
    },
    { title: 'Order', dataIndex: 'order', key: 'order', width: 70, align: 'center' },
    {
      title: 'Active', key: 'active', width: 80, align: 'center',
      render: (_, d) => <Switch size="small" checked={d.isActive !== false} onChange={(v) => toggleActive(d, v)} />,
    },
    {
      title: '', key: 'ops', width: 90, align: 'right',
      render: (_, d) => (
        <Space size={4}>
          <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => setDraft({ doc: d })} /></Tooltip>
          <Popconfirm title={`Delete workflow "${d.key}"?`} onConfirm={() => handleDelete(d.key)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fg-section">
      <AppCard
        icon={<ApartmentOutlined />}
        title="Workflows"
        extra={
          <Space size={8}>
            <Select
              size="small" style={{ minWidth: 160 }} allowClear placeholder="Filter by module"
              options={modules} value={moduleFilter || undefined} onChange={(v) => setModuleFilter(v ?? '')}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={load}>Reload</Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setDraft({ doc: null })}>New Workflow</Button>
          </Space>
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Event-driven behavior per module — reference ids, duplicate checks, snapshots, hierarchy fan-outs,
          board updates, notifications, cache — executed by the platform Workflow Engine on every create/update/delete.
          <Tag style={{ marginLeft: 8 }}>before*</Tag> actions can block the write; <Tag>after*</Tag> actions are best-effort.
        </Text>
      </AppCard>

      <AppCard styles={{ body: { paddingTop: 8 } }}>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        <Spin spinning={loading}>
          <div style={{ marginBottom: 8 }}>
            <Badge count={rows.length} showZero color="var(--color-brand)" /> <Text type="secondary" style={{ fontSize: 12 }}>workflows</Text>
          </div>
          <Table
            rowKey="key" dataSource={rows} columns={columns} pagination={false} size="small" className="fg-table"
            locale={{ emptyText: <Empty description="No workflows configured." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </Spin>
      </AppCard>

      <WorkflowDrawer
        key={draft ? (draft.doc?.key ?? 'new') : 'closed'}
        open={!!draft} doc={draft?.doc} moduleOptions={modules}
        onClose={() => setDraft(null)} onSave={handleSave}
      />
    </div>
  );
}
